import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, Detail, Icon, Label, Pill, PillVariant, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {NovaButtonSizer} from './NovaButtonSize';

const tokens = tokensForThemeProvider.sky.sys;

// One insertable entry: a token, what it is, and optionally where it comes from.
export interface InsertLibraryRow {
	key: string;
	token: string;
	description: string;
	secondary?: string;
	badges?: string[];
	dataCy: string;
	onInsert: () => void;
}

export interface InsertLibraryGroup {
	key: string;
	label: string;
	rows: InsertLibraryRow[];
	// Shown in place of rows when the group has none.
	emptyText?: string;
}

interface NovaInsertLibraryProps {
	// Prefix for every data-cy on the panel: `<prefix>`, `<prefix>-bar`, `<prefix>-body`,
	// `<prefix>-group-<key>`, `<prefix>-<key lower case>-empty`,
	// `<prefix>-target`, `<prefix>-notice` and `<prefix>-empty`.
	dataCy: string;
	title: string;
	// One line beside the title saying what a click does and where it inserts.
	helper: string;
	countLabel: string;
	// The prompt a click inserts into, shown as a pill beside the count. Omitted, there is no pill.
	targetLabel?: string;
	groups: InsertLibraryGroup[];
	// Shown above the groups, for a list that could not be read in full.
	notice?: string;
	// A button beside the notice that reads the list again.
	noticeAction?: {label: string; onPress: () => void};
	// Shown in place of the groups when there are none.
	emptyText?: string;
}

const Container = styled.div`
	border: 1px solid ${tokens.color.border.decorative};
	border-radius: 8px;
	overflow: hidden;
`;

const NoticeRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

// The whole header toggles the panel, not only its chevron.
const Header = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${tokens.spacing['200']};
	padding: ${tokens.spacing['400']};
	cursor: pointer;
`;

const Helper = styled.span`
	min-width: 0;
`;

// The target pill and the count, pushed to the right end of the header.
const HeaderEnd = styled.span`
	margin-left: auto;
	display: flex;
	align-items: center;
	gap: ${tokens.spacing['300']};
`;

const Count = styled.span`
	display: inline-flex;
	flex: none;
	white-space: nowrap;
`;

// A compact Pill: 20px tall with 11px type, so the target sits level with the header's text.
const CompactPill = styled.span`
	display: inline-flex;

	& > * {
		height: 20px;
		min-height: 20px;
		padding: 0 6px;
		box-sizing: border-box;
	}

	& * {
		font-size: 11px;
		line-height: 16px;
	}
`;

const Body = styled.div`
	border-top: 1px solid ${tokens.color.border.decorative};
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

// The group heading: a small uppercase caption in a light grey.
const GroupLabel = styled.span`
	display: block;
	text-transform: uppercase;
	letter-spacing: 1.2px;

	& * {
		color: ${tokens.color.text.disabled.ondisabled};
	}
`;

// The token takes two fifths of the row and its description the rest, so tokens line up down the list.
const Row = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
	gap: 16px;
	align-items: center;
`;

const TokenCell = styled.div`
	display: flex;
	min-width: 0;
`;

const DescriptionCell = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

// Pressing the header or a token keeps the prompt being edited focused, so an insertion lands at its
// caret. The capture handlers run before the button's own and stop the browser moving focus; the
// keyboard still reaches every button.
const keepFocus = {
	onMouseDownCapture: (event: React.MouseEvent) => event.preventDefault(),
	onPointerDownCapture: (event: React.PointerEvent) => event.preventDefault(),
};

// A collapsible list of tokens an author can drop into the prompt they are editing: closed by
// default, opened by pressing anywhere on its header, which says how many entries there are.

export function NovaInsertLibrary({
	dataCy,
	title,
	helper,
	countLabel,
	targetLabel,
	groups,
	notice,
	noticeAction,
	emptyText,
}: NovaInsertLibraryProps): JSX.Element {
	const [expanded, setExpanded] = useState(false);
	const toggle = () => setExpanded(previous => !previous);

	return (
		<Container data-cy={dataCy} data-testid={dataCy}>
			<Header
				role="button"
				tabIndex={0}
				aria-expanded={expanded}
				data-cy={`${dataCy}-bar`}
				data-testid={`${dataCy}-bar`}
				onClick={toggle}
				onKeyDown={(event: React.KeyboardEvent) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						toggle();
					}
				}}
				{...keepFocus}
			>
				<Icon icon={expanded ? 'chevronDown' : 'chevronRight'} size="s" />
				<Label>{title}</Label>
				<Helper data-cy={`${dataCy}-helper`}>
					<Text color="auxiliary">{helper}</Text>
				</Helper>
				<HeaderEnd>
					{targetLabel && (
						<CompactPill data-cy={`${dataCy}-target`}>
							<Pill variant={PillVariant.NEUTRAL}>{targetLabel}</Pill>
						</CompactPill>
					)}
					<Count data-cy={`${dataCy}-count`}>
						<Text color="auxiliary">{countLabel}</Text>
					</Count>
				</HeaderEnd>
			</Header>
			{expanded && (
				<Body data-cy={`${dataCy}-body`} data-testid={`${dataCy}-body`}>
					{notice && (
						<NoticeRow>
							<span data-cy={`${dataCy}-notice`} data-testid={`${dataCy}-notice`}>
								<Text color="error">{notice}</Text>
							</span>
							{noticeAction && (
								<Button emphasis="medium" data-cy={`${dataCy}-retry`} onPress={noticeAction.onPress}>
									{noticeAction.label}
								</Button>
							)}
						</NoticeRow>
					)}
					{groups.length === 0 && notice ? null : groups.length === 0 && emptyText ? (
						<span data-cy={`${dataCy}-empty`} data-testid={`${dataCy}-empty`}>
							<Text color="auxiliary">{emptyText}</Text>
						</span>
					) : (
						groups.map(group => (
							<Group
								key={group.key}
								data-cy={`${dataCy}-group-${group.key}`}
								data-testid={`${dataCy}-group-${group.key}`}
							>
								<GroupLabel>
									<Detail size="2" noWrap>
										{group.label}
									</Detail>
								</GroupLabel>
								{group.rows.length === 0
									? group.emptyText && (
											<span
												data-cy={`${dataCy}-${group.key.toLowerCase()}-empty`}
												data-testid={`${dataCy}-${group.key.toLowerCase()}-empty`}
											>
												<Text color="auxiliary">{group.emptyText}</Text>
											</span>
									  )
									: group.rows.map(row => (
											<Row key={row.key} data-cy={`${row.dataCy}-row`}>
												<TokenCell>
													<NovaButtonSizer $size="row" {...keepFocus}>
														<Button emphasis="medium" data-cy={row.dataCy} onPress={row.onInsert}>
															{row.token}
														</Button>
													</NovaButtonSizer>
												</TokenCell>
												<DescriptionCell>
													<Text size="2">{row.description}</Text>
													{row.secondary && (
														<Detail size="1" color="auxiliary">
															{row.secondary}
														</Detail>
													)}
													{(row.badges ?? []).map(badge => (
														<span key={badge}>
															<Pill variant={PillVariant.NEUTRAL}>{badge}</Pill>
														</span>
													))}
												</DescriptionCell>
											</Row>
									  ))}
							</Group>
						))
					)}
				</Body>
			)}
		</Container>
	);
}
